import output from "_src/engine/output";
import objFactory from "_src/engine/factories/object";
import materialFactory from "_src/client/materials/material_factory";
import events from "_src/engine/events";
import { Geometry, GeometryBuffer } from "_src/engine/geometry.js";
import minmaxUtils from "_src/engine/utility/minmax";

import ObjectWrapper from "_src/object_wrapper";
import CadWrapper from "_src/cad_wrapper";
import PhotoGroupWrapper from "_src/photogroup_wrapper";
import SectionMeasureWrapper from "_src/section_measure_wrapper";
import FrameWrapper from "_src/frame_wrapper";
import Group from "_src/groupable_wrapper";

import loader from "_src/loader";
import loadingProgress from "_src/loading_progress";
import objState from "_src/obj_state";
import appState from "_src/app_state";
import history from "_src/history/manager";
import calcTextureCorrectionMatrix from "_src/texture_correction";
import {cadType, objectType, propertyInitStatus, scanMode} from "_src/constants";
import {colorUtils} from "_src/utils";
import { mat4 } from "_src/libs/gl-matrix";

import reformer from "_src/reformer/reformer";
import saveManager from "_src/save_manager";
import config from "_src/config";
import containersCache from "_src/engine/packers/containers_cache";
import {stencilAction, stencilFunc, stencilValue} from "_src/engine/constants";
import indexedDataBase from "_database";
import monitoring from "_src/monitoring";
import projectTree from "_src/reformer/project_tree";
import DistanceMeasureWrapper from "_src/distance_measure_wrapper";
import DistanceMapWrapper from "_src/distance_map_wrapper";
import DetachedWrapper from "_src/detached_wrapper";

const _useChunkLevels = false;
const _saveChunksState = {
  addPayloads: [],
  updatePayloads: [],
  deletePayloads: [],
  deletedFrames: [],
  projectFiles: [],
};

export default {

  _objects: [],
  _groups: [],
  _photoGroups: [],
  _measures: [],

  _scene: null,

  _updatedChunks: new Set(),
  _newChunks: new Set(),

  _chunksChildrenNeighbours: new WeakMap(),
  _chunksParentNeighbours: new WeakMap(),

  _objectInitialized: new WeakSet(),
  _needDupliVerticesUpdate: new WeakSet(),
  _normalsBuilt: new WeakSet(),

  _totalNumLoadingGeometries: 0,
  _numLoadedGeometries: 0,

  _objectsUpdatePromises: new Map(),
  _objectsSyncPromises: new Map(),
  _objectSyncTriggers : [],

  _scansDecimationRatio: 1.0,
  _sliceToFrames: new Map(),
  _objectsLoadingsNumber: new WeakMap(),
  _decimatedFramesSets: new WeakSet(),
  _frameToPriority: new WeakMap(),

  _saveID: 0,

  async openProject(projectPath) {
    try {
      const json = await loader.load(projectPath, loader.JSON, null);
      if (!json) {
        return null;
      }

      reformer.setProjectPath(projectPath);
      if (config.cacheUnloadedData) await indexedDataBase.cleanup();

      return json;
    } catch (error) {
      if (!loader.isLegalError(error)) {
        throw error;
      }

      return null;
    }
  },

  async closeProject() {

    const objects = this.getObjects();

    for (const object of objects) {
      await object.unloadData();
      events.emit("object_removed", object);
    }

    // NOTE: we can close current project before JSON is loaded
    loader.abortByType(loader.JSON);
    loader.abortByType(loader.GEOMETRY);

    this._totalNumLoadingGeometries = 0;
    this._numLoadedGeometries = 0;

    history.restart();
    reformer.cleanup();

    this._clearDataContainers();

  },

  async loadExternalModel(path, progress = null) {
    const loadPayload = {fileProgress: progress, useWorkers: true};
    const objectsData = await loader.load(path, loader.EXTERNAL, loadPayload);
    if (objectsData.length === 0) throw new Error("No objects in file");
    const objectData = objectsData[0];

    const model = objFactory.createDrawable(null);
    const materialType = Boolean(objectData.material.baseTexture) && Boolean(objectData.material.metallicRoughnessTexture) ? materialFactory.PBR : materialFactory.ASTUDIO;
    await materialFactory.resetMaterial(materialType, model, model.properties);
    const objectWrapper = new DetachedWrapper({
      model   : model,
    });
    const frameWrapper = new FrameWrapper({
      model : model,
    });
    const texturingProgress = progress ? (progressVal) => progress.emit(progress.TEXTURING, progressVal) : null;
    await frameWrapper.initByExternalData(objectData, texturingProgress);
    objectWrapper.frames.push(frameWrapper);

    model.setDiffuseColor(0.8, 0.8, 0.8);
    model.setVisibility(true);
    if (objectData.geometry.hasLayoutItem("a_normal")) appState.setStateValue("shadingType", appState.SHT_SMOOTH);

    this._addObject(objectWrapper);
    events.emit("objects_initialized");
  },

  cleanup() {

    this._clearDataContainers();

    this._scene = null;

    this._saveID = 0;

  },

  _clearDataContainers() {

    for (const object of this.getObjects()) {
      object.cleanup();
    }
    this._objects.length = 0;
    this._photoGroups.length = 0;
    this._measures.length = 0;
    this._groups.length = 0;

    this._scansDecimationRatio = 1.0;
    this._sliceToFrames.clear();

    this._newChunks.clear();
    this._updatedChunks.clear();

    this._objectsUpdatePromises.clear();
    this._objectsSyncPromises.clear();
    this._objectSyncTriggers.length = 0;
    this._validateObjectsStates();

  },

  scheduleObjectDrawQueries(object, shouldRun) {
    if (!config.drawQueries) return;

    // we avoid using _traverseChildren here as it is a bit slower and
    // this method can get quite hot sometimes.
    for (let frame of object.frames) {
      const model = frame.model;
      const chunks = model.chunks;
      for (let chunk of chunks) {
        model.setScheduleDrawQuery(chunk, shouldRun);
      }
    }

    if (shouldRun) {
      this._scene.batcher.needsSort = true;
    }

  },

  createObjects() {
    const scanList = projectTree.getPropertiesByType(objectType.SCAN).concat(projectTree.getPropertiesByType(objectType.POINTCLOUD));
    const fusionList = projectTree.getPropertiesByType(objectType.FUSION);

    for (const scanProperty of scanList) {
      if (appState.getStateValue("scanMode") !== scanMode.TRIANGLES && scanProperty.getValue("surfelsfile") !== null) {
        this._createSurfelsWrapper(scanProperty);
      } else {
        this._createScanWrapper(scanProperty);
      }
    }

    for (const fusionProperty of fusionList) {
      this._createFusionWrapper(fusionProperty);
    }

    if (config.enableRendering) {
      const cadList = projectTree.getPropertiesByType(objectType.CAD);
      const photoGroupList = projectTree.getPropertiesByType(objectType.PHOTO_GROUP);
      const sectionMeasuresList = projectTree.getPropertiesByType(objectType.SECTION);
      const distanceMeasuresList = projectTree.getPropertiesByType(objectType.LINEAR_MEASURE).concat(projectTree.getPropertiesByType(objectType.GEODESIC_MEASURE));
      const distanceMapList = projectTree.getPropertiesByType(objectType.DISTANCE_MAP);
      const groupsList = projectTree.getPropertiesByType(objectType.GROUP);

      for (const cadData of cadList) {
        this._createCADWrapper(cadData);
      }
      for (const groupProperty of groupsList) {
        this._createGroupWrapper(groupProperty);
      }
      for (const photoGroupProperty of photoGroupList) {
        this._createPhotoGroupWrapper(photoGroupProperty);
      }
      for (const sectionMeasureProperty of sectionMeasuresList) {
        this._createSectionMeasureWrapper(sectionMeasureProperty);
      }
      for (const distanceMeasureProperty of distanceMeasuresList) {
        this._createDistanceMeasureWrapper(distanceMeasureProperty);
      }
      for (const distanceMapProperty of distanceMapList) {
        this._createDistanceMapWrapper(distanceMapProperty);
      }
    }
  },

  async initObjects() {
    const combinePromises = [];
    for (const object of this._objects.concat(this._photoGroups).concat(this._measures)) {
      if (object.getVisibility()) {
        combinePromises.push(projectTree.combineObjectStructure(object.getID()));
      }
    }
    await Promise.all(combinePromises);

    for (const object of this._objects) {
      if (this._objectInitialized.has(object) || object.getProperty("initStatus") !== propertyInitStatus.COMPLETE) {
        continue;
      }

      switch (object.type) {
        case objectType.FUSION:
          this._initFusionWrapper(object);
          break;
        case objectType.SCAN:
          this._initScanWrapper(object);
          break;
        case objectType.SURFELS:
          this._initSurfelsWrapper(object);
          break;
        case objectType.CAD:
          await this._initCADWrapper(object);
          break;
      }

      this._objectInitialized.add(object);
    }

    this._initDecimationMap();
    events.emit("objects_initialized");
  },

  prepareProjectLoading(json) {
    reformer.initProjectStructure(json);
    this.createObjects();
  },

  async updateProject(path) {
    const json = await loader.load(path, loader.JSON, null);
    if (!json) {
      return null;
    }
    if (config.cacheUnloadedData) await indexedDataBase.cleanup();
    reformer.setProjectPath(path);

    const updatedObjects = await this._updateProjectStructure(json);
    return updatedObjects;
  },

  _updateFrame(frame, oldData, data, object) {
    frame.numPolygons = 0;
    frame.numVertices = 0;
    let updated = false;

    updated = updateModelTransform(frame.model, data) || updated;
    for (const chunkData of data.getValue("geometries")) {
      frame.numPolygons += chunkData.getValue("levels")[0].getValue("triangles");
      frame.numVertices += chunkData.getValue("levels")[0].getValue("points");

      if (frame.model.chunks.find(chunk => chunk.getID() === chunkData.getValue("id"))) {
        continue;
      }

      frame.loaded = false;
      updated = true;
      //Note: chunk will be added during frame load
    }

    const chunksToDelete = [];
    for (const chunk of frame.model.chunks) {
      if (!data.getValue("geometries").find(chunkData => chunkData.getValue("id") === chunk.getID())) {
        chunksToDelete.push(chunk);
        updated = true;
      }
    }

    for (const chunk of chunksToDelete) {
      frame.deleteChunk(chunk, false);
    }

    const geometryUpdated = this._updateFrameGeometryData(frame, oldData, data);
    const textureUpdated = this._updateFrameTextureData(frame, oldData, data, object);

    updated = geometryUpdated || textureUpdated || updated;

    return updated;
  },

  _updateFrameGeometryData(frame, oldData, data) {
    let updated = false;

    for (const chunkData of data.getValue("geometries")) {
      const chunkObject = frame.model.chunks.find(chunk => chunk.getID() === chunkData.getValue("id"));
      if (!chunkObject) {
        continue;
      }
      const oldChunkData = oldData.getValue("geometries").find(oldChunkData => oldChunkData.getValue("id") === chunkData.getValue("id"));

      const oldZeroDataLevelHash = oldChunkData.getValue("levels")[0].getValue("hash");
      const zeroDataLevelHash = chunkData.getValue("levels")[0].getValue("hash");

      const upToDate = chunkObject.geometry && oldZeroDataLevelHash === zeroDataLevelHash;

      if (!upToDate) {
        this._normalsBuilt.delete(chunkObject);
        chunkObject.needsReload = true;
        frame.loaded = false;
        updated = true;
        chunkObject.cleanup();
      }
    }

    return updated;
  },

  _updateFrameTextureData(frame, oldData, data, object) {
    let updated = false;
    let shouldLoadTexture = false;

    const texturesData = data.getValue("textures") || [];
    const oldTexturesData = oldData.getValue("textures") || [];

    if (texturesData.length && !oldTexturesData.length) {
      frame.addTextureInfo(texturesData[0]);
      shouldLoadTexture = true;
      updated = true;
    } else if (!texturesData.length && oldTexturesData.length) {
      frame.dropTextures();
      updated = true;
    } else if (texturesData.length && oldTexturesData.length) {
      const newTextureLevels = texturesData[0].getValue("levels");
      const oldTextureLevels = oldTexturesData[0].getValue("levels");
      const newTextureHash = newTextureLevels[newTextureLevels.length - 1].getValue("hash");
      const oldTextureHash = oldTextureLevels[oldTextureLevels.length - 1].getValue("hash");
      if (newTextureHash !== oldTextureHash) {
        frame.textureList.length = 0;
        frame.addTextureInfo(texturesData[0]);
        object.setFrameTemporaryTexture(frame);
        shouldLoadTexture = true;
        updated = true;
      }
    }

    if (shouldLoadTexture) {
      frame.loadTexture();
    }

    return updated;
  },

  _updateFrames(object, oldFramesNode, framesNode){
    let updated = false;

    for (const frameData of framesNode) {
      const frameObject = object.frames.find(frame => frame.getID() === frameData.getValue("id"));
      if (frameObject) {
        const oldFrameData = oldFramesNode.find(frameProperty => frameProperty.getValue("id") === frameObject.getID());
        updated = this._updateFrame(frameObject, oldFrameData, frameData, object) || updated;
        continue;
      }

      const frameWrapper = this._createFrameWrapper(frameData, object.getID());
      frameWrapper.model.setDiffuseColor(...object.getColor());
      frameWrapper.model.shader = object.model.shader;

      frameWrapper.updateBoundingsByTransform();
      object.addFrame(frameWrapper);
      object.model.add(frameWrapper.model);
      updated = true;
    }

    const framesToUnload = [];
    for (const frame of object.frames) {
      if (!framesNode.find(frameData => frameData.getValue("id") === frame.getID())) {
        framesToUnload.push(frame);
        updated = true;
      }
    }

    for (const frame of framesToUnload) {
      object.removeFrame(frame);
    }

    return updated;
  },

  async _updateObject(object, oldProperty, property) {
    let updated = false;
    updated = updateModelTransform(object.model, property) || updated;

    let geometryNode = null;
    let oldGeometryNode = null;

    switch (object.type) {
      case objectType.FUSION:
        geometryNode = property.getValue("geometriesfile");
        oldGeometryNode = oldProperty.getValue("geometriesfile");
        break;
      case objectType.SURFELS:
        geometryNode = property.getValue("surfelsfile");
        oldGeometryNode = oldProperty.getValue("surfelsfile");
        break;
      case objectType.SCAN:
        geometryNode = property.getValue("framesfile");
        oldGeometryNode = oldProperty.getValue("framesfile");
        break;
      case objectType.CAD:
        //cad's geometry update is not implemented
        return updated;

      default:
        output.error(output.DEVELOPERS_ERROR, "update for type " + object.type + " is not supported yet");
        return false;
    }

    if (!this._objectInitialized.has(object)) {
      return updated;
    }

    const oldGeometryHash = oldGeometryNode["hash"];
    const newGeometryHash = geometryNode["hash"];
    const geometryUpToDate = oldGeometryHash && newGeometryHash && oldGeometryHash === newGeometryHash;

    //Note: this is hack because of texture info is in main file. Its better to move it to the frame
    let fusionTextureUpToDate = true;
    if (object.type === objectType.FUSION) {
      const oldTextureExists = oldProperty.getValue("textures").length > 0;
      const newTextureExists = property.getValue("textures").length > 0;

      const oldTextureHash = oldTextureExists ? oldProperty.getValue("textures")[0]["levels"][0]["hash"] : "";
      const newTextureHash = newTextureExists ? property.getValue("textures")[0]["levels"][0]["hash"] : "";
      fusionTextureUpToDate = oldTextureHash === newTextureHash;
    }

    const oldFramesProperty = oldProperty.getValue("frames");
    if (geometryUpToDate && fusionTextureUpToDate) {
      property.setValue("frames", oldFramesProperty);
      property.setValue("initStatus", propertyInitStatus.COMPLETE);
    } else {
      await projectTree.combineObjectStructure(object.getID());
      const framesProperty = property.getValue("frames");

      if (objState.checkIsUpdating(object)) {
        await objState.getUpdatePromise(object);
      }
      updated = this._updateFrames(object, oldFramesProperty, framesProperty) || updated;
    }

    return updated;
  },

  async _updateProjectStructure(json) {
    let updatedObjects = [];

    const oldPhotoGroupsList = projectTree.getPropertiesByType(objectType.PHOTO_GROUP);
    const oldDistanceMeasureList = projectTree.getPropertiesByType(objectType.LINEAR_MEASURE).concat(projectTree.getPropertiesByType(objectType.GEODESIC_MEASURE));
    const oldSectionMeasureList = projectTree.getPropertiesByType(objectType.SECTION);
    const oldDistanceMapList = projectTree.getPropertiesByType(objectType.DISTANCE_MAP);
    const oldCadList = projectTree.getPropertiesByType(objectType.CAD);
    const oldScanList = projectTree.getPropertiesByType(objectType.SCAN).concat(projectTree.getPropertiesByType(objectType.POINTCLOUD));
    const oldFusionList = projectTree.getPropertiesByType(objectType.FUSION);

    reformer.initProjectStructure(json);

    const photoGroupsList = projectTree.getPropertiesByType(objectType.PHOTO_GROUP);
    const distanceMeasureList = projectTree.getPropertiesByType(objectType.LINEAR_MEASURE).concat(projectTree.getPropertiesByType(objectType.GEODESIC_MEASURE));
    const sectionMeasureList = projectTree.getPropertiesByType(objectType.SECTION);
    const distanceMapList = projectTree.getPropertiesByType(objectType.DISTANCE_MAP);
    const cadList = projectTree.getPropertiesByType(objectType.CAD);
    const scanList = projectTree.getPropertiesByType(objectType.SCAN).concat(projectTree.getPropertiesByType(objectType.POINTCLOUD));
    const fusionList = projectTree.getPropertiesByType(objectType.FUSION);
    const groupsList = projectTree.getPropertiesByType(objectType.GROUP);

    const updateObjectList = async (objectPropertyList, oldObjectPropertyList, objectsList, preUpdateFn, updateFn, createFn, removeFn) => {
      for (const objectProperty of objectPropertyList) {
        const object = objectsList.find(object => object.getID() === objectProperty.getValue("id"));
        if (object) {
          if (preUpdateFn) await preUpdateFn(object);
          if (updateFn) {
            const oldObjectProperty = oldObjectPropertyList.find(property => property.getValue("id") === object.getID());
            await updateFn(object, oldObjectProperty, objectProperty);
          }
        } else {
          const newObject = createFn(objectProperty);
          updatedObjects.push(newObject);
        }
      }

      const objectsToUnload = [];
      for (const object of objectsList) {
        if (!objectPropertyList.find(property => property.getValue("id") === object.getID())) {
          objectsToUnload.push(object);
        }
      }
      for (const object of objectsToUnload) {
        await removeFn(object);
      }
    }

    const objectsPreUpdateFn = async (object) => {
      if (objState.checkIsUpdating(object)) {
        await objState.getUpdatePromise(object);
      }
    }
    const objectsUpdateFn = async (object, oldObjectProperty, objectProperty) => {
      const updated = await this._updateObject(object, oldObjectProperty, objectProperty);
      if (updated) {
        updatedObjects.push(object);
      }
    }
    const scanCreateFn = (objectProperty) => {
      if (appState.getStateValue("scanMode") !== scanMode.TRIANGLES && objectProperty.getValue("surfelsfile") !== null) {
        return this._createSurfelsWrapper(objectProperty);
      } else {
        return this._createScanWrapper(objectProperty);
      }
    }
    const scanObjectsList = this.getObjects().filter(object => object.type === objectType.SCAN || object.type === objectType.SURFELS);
    const fusionObjectsList = this.getObjects().filter(object => object.type === objectType.FUSION);
    const cadObjectsList = this.getObjects().filter(object => object.type === objectType.CAD);
    const distanceMeasureObjectsList = this.getMeasures().filter(object => object.type === objectType.LINEAR_MEASURE || object.type === objectType.GEODESIC_MEASURE);
    const sectionMeasuresObjectsList = this.getMeasures().filter(object => object.type === objectType.SECTION);
    const distanceMapsObjectsList = this.getMeasures().filter(object => object.type === objectType.DISTANCE_MAP);

    await updateObjectList(scanList, oldScanList, scanObjectsList, objectsPreUpdateFn, objectsUpdateFn, scanCreateFn, o=>this.removeObject(o));
    await updateObjectList(fusionList, oldFusionList, fusionObjectsList, objectsPreUpdateFn, objectsUpdateFn, p => this._createFusionWrapper(p), o=>this.removeObject(o));
    await updateObjectList(cadList, oldCadList, cadObjectsList, objectsPreUpdateFn, objectsUpdateFn, p=>this._createCADWrapper(p), o=>this.removeObject(o));
    await updateObjectList(photoGroupsList, oldPhotoGroupsList, this.getPhotoGroups(), null, _updatePhotoGroup, p=>this._createPhotoGroupWrapper(p), pg=>this._removePhotoGroup(pg));
    await updateObjectList(groupsList, null, this.getGroups(), null, null, p=>this._createGroupWrapper(p), g=>this._removeGroup(g));
    await updateObjectList(distanceMeasureList, oldDistanceMeasureList, distanceMeasureObjectsList, null, _updateMeasure, p=>this._createDistanceMeasureWrapper(p), m=>this._removeMeasure(m));
    await updateObjectList(sectionMeasureList, oldSectionMeasureList, sectionMeasuresObjectsList, null, _updateMeasure, p=>this._createSectionMeasureWrapper(p), m=>this._removeMeasure(m));
    await updateObjectList(distanceMapList, oldDistanceMapList, distanceMapsObjectsList, null, _updateMeasure, p=>this._createDistanceMapWrapper(p), m=>this._removeMeasure(m));

    updatedObjects = updatedObjects.filter(object => object.type === objectType.FUSION || object.type === objectType.CAD || object.type === objectType.SCAN || object.type === objectType.SURFELS);
    for (const object of updatedObjects) {
      object.setState(objState.OS_OCTREE, false);
      object.setState(objState.OS_HALF_EDGE, false);
      object.setState(objState.OS_NORMAL, false);
    }

    return updatedObjects;
  },

  async updateScanMode(mode) {
    let needInitObjects = false;
    for (const object of this.getObjects().slice()) {
      const someDataLoaded = object.loaded || object.checkNeedsLoadFrames();
      const needReinitObject = object.type === objectType.SCAN && this.hasScanSurfels(object) && mode !== scanMode.TRIANGLES ||
          object.type === objectType.SURFELS && mode === scanMode.TRIANGLES;
      const needReloadObject = object.type === objectType.SURFELS && someDataLoaded &&
          (mode === scanMode.SURFELS_HIGH && object.geometryLevel === 1 || mode === scanMode.SURFELS_MID && this.hasObjectLods(object) && object.geometryLevel === 0);

      if (needReloadObject || needReinitObject) {
        await object.unloadData();
      }

      if (needReinitObject) {
        releaseSlicedCache(object);
        this._objects.splice(this._objects.indexOf(object), 1);
        events.emit("object_removed", object);

        const property = projectTree._getProperty(object.getID()); //hack
        property.getValue("frames").length = 0;
        property.setValue("initStatus", propertyInitStatus.INCOMPLETE);
        if (mode === scanMode.TRIANGLES) this._createScanWrapper(property);
        else this._createSurfelsWrapper(property);
        needInitObjects = true;
      }
    }

    if (needInitObjects) {
      await this.initObjects();
    }
  },

  loadObjectData(object, level = 0) {
    let numLoadings = this._objectsLoadingsNumber.get(object) + 1;
    this._objectsLoadingsNumber.set(object, numLoadings);

    object.updateTextureUsage();

    return object.loadData(object.getProperty("frames"), level).then(() => {
      this.onObjectLoaded(object);
    }).catch((error) => {
       if (!loader.isLegalError(error)) {
         output.error(output.LOADING_ERROR, "Couldn't load object data. Reason: " + error.message);
         return;
       }
       if (loader.isNetworkError(error)) {
         appState.setStateValue("hasLoadingErrors", true);
         output.warn("Couldn't load project. Editing is forbidden.");
         return;
       }
       if (loader.isTotalAborted(error)) {
         return;
       }
       this.onObjectLoaded(object);
    });
  },

  onObjectLoaded(object) {

    const numLoadings = Math.max(this._objectsLoadingsNumber.get(object) - 1, 0);
    this._objectsLoadingsNumber.set(object, numLoadings);

    if (numLoadings !== 0) {
      return;
    }

    releaseSlicedCache(object);
    object.loaded = true;

    for (let frame of object.frames) {
      const model = frame.model;
      this._calculateNeighbourChunks(model.chunks);
    }

    this.applyState(object);

    events.emit("object_loaded", object);
  },

  _createScanWrapper(property) {
    const model = objFactory.createDrawable(null);
    if (config.enableRendering) {
      materialFactory.resetMaterial(materialFactory.ASTUDIO, model, model.properties);
      const shader = model.shader;

      shader.setTextureProjectionMatrix(property.getValue("projection"));
      const { colorCorrectionMatrix, corrCoeff } = calcTextureCorrectionMatrix(property, mat4.create());
      if (corrCoeff !== -1) {
        shader.setDirective("COLOR_CORRECTION", true);
        shader.setColorCorrectionMatrix(colorCorrectionMatrix);
      }
    }

    const scanWrapper = new ObjectWrapper({
      type        : objectType.SCAN,
      id          : property.getValue("id"),
      model       : model,
      usetexture  : property.getValue("numTexturedFrames") > 0
    });

    model.setMatrix(property.getValue("transform"));
    model.updateMatrix();
    this._addObject(scanWrapper);

    return scanWrapper;
  },

  _initScanWrapper(scanWrapper) {
    for (const frameProperty of scanWrapper.getProperty("frames")) {

      const frameWrapper = this._createFrameWrapper(frameProperty, scanWrapper.getID());
      frameWrapper.model.setDiffuseColor(...scanWrapper.getColor());
      scanWrapper.model.add(frameWrapper.model);

      frameWrapper.model.updateMatrix();
      frameWrapper.updateBoundingsByTransform();

      if (config.enableRendering) {
        materialFactory.resetMaterial(materialFactory.ASTUDIO, frameWrapper.model, frameWrapper.model.properties);
        frameWrapper.model.shader = scanWrapper.model.shader;
      }

      scanWrapper.addFrame(frameWrapper);

    }

    if (appState.getStateValue("colorMode") === appState.CM_FRAME_COLOR) {
      scanWrapper.applyUniqueColorToFrames();
    } else {
      scanWrapper.applyObjectColorToFrames();
    }

    this._objectsLoadingsNumber.set(scanWrapper, 0);

    events.emit("objects_updated", [scanWrapper]);
  },

  _createCADWrapper(property) {
    const isPrimitive = Boolean(property.getValue("cadType") !== cadType.MODEL);

    const mainModel = objFactory.createDrawable(null);
    const mainMaterial = isPrimitive ? materialFactory.CAD : materialFactory.ASTUDIO;
    const edgesModel = isPrimitive ? null : objFactory.createDrawable(null);
    if (config.enableRendering) {
      materialFactory.resetMaterial(mainMaterial, mainModel, mainModel.properties);
      if (edgesModel) materialFactory.resetMaterial(materialFactory.CAD_EDGES, edgesModel, edgesModel.properties);
    }

    const CADWrapper = new CadWrapper({
      id          : property.getValue("id"),
      model       : mainModel,
      edgesModel  : edgesModel,
    });

    mainModel.setMatrix(property.getValue("transform"));
    mainModel.updateMatrix();
    if (edgesModel) {
      edgesModel.setMatrix(property.getValue("transform"));
    }

    this._addObject(CADWrapper);
    return CADWrapper;
  },

  async _initCADWrapper(CADWrapper) {
    const material = CADWrapper.isPrimitive() ? materialFactory.CAD : materialFactory.ASTUDIO;

    for (const frameProperty of CADWrapper.getProperty("frames")) {

      const frameWrapper = this._createFrameWrapper(frameProperty, CADWrapper.getID());
      frameWrapper.model.setDiffuseColor(...CADWrapper.getColor());
      CADWrapper.model.add(frameWrapper.model);

      frameWrapper.model.updateMatrix();
      frameWrapper.updateBoundingsByTransform();

      if (config.enableRendering) {
        materialFactory.resetMaterial(material, frameWrapper.model, frameWrapper.model.properties);
        frameWrapper.model.shader = CADWrapper.model.shader;
      }

      CADWrapper.addFrame(frameWrapper);

    }

    if (appState.getStateValue("colorMode") === appState.CM_FRAME_COLOR) {
      CADWrapper.applyUniqueColorToFrames();
    } else {
      CADWrapper.applyObjectColorToFrames();
    }

    this._objectsLoadingsNumber.set(CADWrapper, 0);

    events.emit("objects_updated", [CADWrapper]);
  },

  _createPhotoGroupWrapper(property) {
    const photoGroupWrapper = new PhotoGroupWrapper({id: property.getValue("id")});
    this._photoGroups.push(photoGroupWrapper);
    return photoGroupWrapper;
  },

  _createSectionMeasureWrapper(property) {
    const sectionMeasureWrapper = new SectionMeasureWrapper({id: property.getValue("id")});
    this._measures.push(sectionMeasureWrapper);
    return sectionMeasureWrapper;
  },

  _createDistanceMeasureWrapper(property) {
    const distanceMeasureWrapper = new DistanceMeasureWrapper({
      id: property.getValue("id"),
      type: property.getValue("type"),
    });
    this._measures.push(distanceMeasureWrapper);
    return distanceMeasureWrapper;
  },

  _createDistanceMapWrapper(property) {
    const distanceMapWrapper = new DistanceMapWrapper({id: property.getValue("id")});
    this._measures.push(distanceMapWrapper);
    return distanceMapWrapper;
  },

  _createFusionWrapper(property) {
    const model = objFactory.createDrawable(null);
    if (config.enableRendering) {
      materialFactory.resetMaterial(materialFactory.ASTUDIO, model, model.properties);
    }

    const fusionWrapper = new ObjectWrapper({
      type        : objectType.FUSION,
      model       : model,
      id          : property.getValue("id"),
      usetexture  : property.getValue("numTexturedFrames") > 0
    });
    model.setMatrix(property.getValue("transform"));
    model.updateMatrix();
    this._addObject(fusionWrapper);
    return fusionWrapper;
  },

  _initFusionWrapper(fusionWrapper) {
    const framesProperty = fusionWrapper.getProperty("frames");
    const frameWrapper = this._createFrameWrapper(framesProperty[0], fusionWrapper.getID());
    frameWrapper.model.setDiffuseColor(...fusionWrapper.getColor());
    frameWrapper.model.shader = fusionWrapper.model.shader;

    fusionWrapper.model.add(frameWrapper.model);
    frameWrapper.model.updateMatrix();
    frameWrapper.updateBoundingsByTransform();
    fusionWrapper.addFrame(frameWrapper);

    this._objectsLoadingsNumber.set(fusionWrapper, 0);
    events.emit("objects_updated", [fusionWrapper]);
  },

  _createSurfelsWrapper(property) {
    const model = objFactory.createDrawable(null);
    if (config.enableRendering) {
      materialFactory.resetMaterial(materialFactory.SURFELS, model, model.properties);
    }

    const surfelsWrapper = new ObjectWrapper({
      type        : objectType.SURFELS,
      id          : property.getValue("id"),
      model       : model,
      usetexture  : false
    });
    model.setMatrix(property.getValue("transform"));
    model.updateMatrix();
    this._addObject(surfelsWrapper);
    return surfelsWrapper;
  },

  _initSurfelsWrapper(surfelsWrapper) {
    const framesProperty = surfelsWrapper.getProperty("frames");
    const frameWrapper = this._createFrameWrapper(framesProperty[0], surfelsWrapper.getID());
    frameWrapper.model.setDiffuseColor(...surfelsWrapper.getColor());
    frameWrapper.model.shader = surfelsWrapper.model.shader;
    surfelsWrapper.model.add(frameWrapper.model);
    frameWrapper.model.updateMatrix();
    frameWrapper.updateBoundingsByTransform();
    surfelsWrapper.addFrame(frameWrapper);
    this._objectsLoadingsNumber.set(surfelsWrapper, 0);

    events.emit("objects_updated", [surfelsWrapper]);
  },

  _createFrameWrapper(property, parentID) {

    const model = objFactory.createDrawable(null);
    model.setReflectivity(config.diffuseReflectivity);

    const frameWrapper = new FrameWrapper({
      id         : property.getValue("id"),
      parentID   : parentID,
      model
    });

    model.setMatrix(property.getValue("transform"));

    for (const texture of property.getValue("textures")) {
      frameWrapper.addTextureInfo(texture);
    }

    for (const geometry of property.getValue("geometries")) {
      frameWrapper.expandLocalMinMax(geometry.getValue("box"));

      const levels = geometry.getValue("levels");
      if (levels.length) {
        // NOTE: there are no levels for just duplicated objects
        frameWrapper.numPolygons += levels[0].getValue("triangles");
        frameWrapper.numVertices += levels[0].getValue("points");
      }
    }

    return frameWrapper;

  },

  _incrementNumLoadedGeometries() {

    ++this._numLoadedGeometries;

  },

  applyState(object) {

    let syncPromise = this._getSyncPromise(object);

    if (syncPromise) {
      return syncPromise;
    }

    syncPromise = this._applyState(object);

    this._storeSyncPromise(object, syncPromise);

    return syncPromise.then(() => {
      this._removeSyncPromise(object);
      events.emit("object_state_applied", object);
    });

  },

  _applyState(object) {

    return new Promise((resolve) => {
      this._storeSyncTrigger(resolve);
    }).then(() => {
      return this._updateObjectStates(object);
    });

  },

  _updateObjectStates(object) {
    if (objState.checkIsUpdating(object)) {
      return objState.getUpdatePromise(object);
    }

    const updatePromise = this._asyncObjectUpdate(object).then(() => {
      this._removeUpdatePromise(object);
      this._validateObjectsStates();
    });

    this._storeUpdatePromise(object, updatePromise);
    return updatePromise;
  },

  async _asyncObjectUpdate(object) {
    if (config.viewOnlyMode) {
        return;
    }

    if (!this.checkIsReady(object) || !object.getVisibility()) {
      return;
    }

    return objState.update(object);
  },

  setObjectState(state, value) {
    if (![objState.OS_NORMAL, objState.OS_OCTREE, objState.OS_HALF_EDGE].includes(state)) {
      return;
    }

    for (const object of this.getObjects()) {
      object.setState(state, value);
    }
  },

  updateObjectsStates() {

    let trigger = this._getActiveSyncTrigger();

    while(trigger) {
      trigger();
      trigger = this._getActiveSyncTrigger();
    }

  },

  clearLoader() {
    loader.abort();
  },

  getBBox(pred) {

    const objects = pred ? this.getObjects().filter(pred): this.getObjects();

    const minmax = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];

    for (let i = 0; i < objects.length; ++i) {
      minmaxUtils.expandMinmax(minmax, objects[i].getWorldMinmax());
    }

    if (!minmaxUtils.isValid(minmax)) {
      return minmaxUtils.setDefaultValue(minmax);
    }

    return minmax;

  },

  getPhotoGroups() {
    return this._photoGroups;
  },

  getMeasures() {
    return this._measures;
  },

  _getObjectSizeData(object) {
    const sizeData = {
      surfels: object.getProperty("surfelsSize"),
      textured: object.getProperty("texturedSize"),
      untextured: object.getProperty("untexturedSize"),
      total: object.getProperty("datasize"),
    };
    return sizeData;
  },

  createDistanceMapMeta(name, uuid, minDistance, maxDistance, dstUuid, searchDistance) {
    return {
      "uuid" : uuid,
      "name" : name,
      "dstUuid" : dstUuid,
      "annotation" : [],
      "minDistance" : minDistance,
      "maxDistance" : maxDistance,
      "geometries" : [],
      "searchDistance" : searchDistance
    }
  },

  getObjectByID(id) {
    return this._objects.find(object => object.getID() === id) || null;
  },

  getObjectByUUID(uuid) {
    return this._objects.find(object => object.getUUID() === uuid) || null;
  },

  getGroupByID(id) {
    return this._groups.find(group => group.getID() === id) || null;
  },

  getPhotoGroupByID(id) {
    return this._photoGroups.find(photoGroup => photoGroup.getID() === id) || null;
  },

  getMeasureByID(id) {
    return this._measures.find(measure => measure.getID() === id) || null;
  },

  _addObject(object) {
    this._objects.push(object);
    events.emit("object_added", object);
  },

  async removeObject(object) {

    object.unloadData();
    for (const frame of object.frames) {
      await frame.geometryPromise;
    }

    projectTree.removeObject(object.getID());
    this._objects.splice(this._objects.indexOf(object), 1);

    events.emit("object_removed", object);
  },

  getObjects() {
    return this._objects;
  },

  getGroups() {
    return this._groups;
  },

  updateFramesVisibility(object) {
    const allFramesMode = appState.getStateValue("showAllFrames");
    const forceNonTexturedFrames = object.type !== objectType.SCAN && !object.hasTextures();
    const forceKeyFrames = object.type === objectType.SCAN && !object.hasTextures();

    if (!object.getVisibility()) {
      object.hideAllFrames();
    } else {
      if (allFramesMode || forceNonTexturedFrames) {
        object.showNonTexturedFrames();
      } else {
        if (forceKeyFrames) {
          if (object.hasKeyFrames) {
            object.hideNonTexturedFrames();
            object.showKeyFrames();
          } else {
            object.showNonTexturedFrames();
          }
        } else {
          object.hideNonTexturedFrames();
        }
      }
    }
  },

  updateNonTexturedFrames(object) {
    this.updateFramesVisibility(object);
    return this.updateDecimation();
  },

  checkIsReadyToSelect(object) {

    if (!object.getVisibility()) {
      return false;
    }

    if (object.getLockState()) {
      return false;
    }

    if (!this.checkIsReady(object)) {
      return false;
    }

    if (object.type === objectType.CAD) {
      return false;
    }

    if (object.getProperty("type") === objectType.POINTCLOUD) {
      return false;
    }

    return true;

  },

  checkIsReadyToSmooth(object) {
    return object.type === objectType.FUSION && this.checkIsReadyToSelect(object);
  },

  setDrawDepth(enabled) {
    for (const object of this.getObjects()) {
      object.model.shader.setDirective("RGB_DEPTH", enabled);
    }
  },

  setWriteDistanceMap(enabled) {
    for (const object of this.getObjects()) {
      object.model.shader.setDirective("DM_OUTPUT", enabled);
    }
  },

  _storeUpdatePromise(object, promise) {
    this._objectsUpdatePromises.set(object, promise);
    this._validateObjectsStates();
  },

  _removeUpdatePromise(object) {
    this._objectsUpdatePromises.delete(object);
    this._validateObjectsStates();
  },

  _storeSyncTrigger(trigger) {
    this._objectSyncTriggers.push(trigger);
  },

  _getActiveSyncTrigger() {
    return this._objectSyncTriggers.pop();
  },

  _storeSyncPromise(object, promise) {
    this._objectsSyncPromises.set(object, promise);
  },

  _getSyncPromise(object) {
    return this._objectsSyncPromises.get(object);
  },

  _removeSyncPromise(object) {
    this._objectsSyncPromises.delete(object);
  },

  _validateObjectsStates() {
    appState.setStateValue("updatingStates", this._objectsUpdatePromises.size !== 0);
  },

  markChunkAsNew(chunk) {
    this._newChunks.add(chunk);
  },

  markChunkAsUpdated(chunk) {
    this._updatedChunks.add(chunk);
  },

  isNewChunk(chunk) {
    return this._newChunks.has(chunk);
  },

  isUpdatedChunk(chunk) {
    return this._updatedChunks.has(chunk);
  },

  removeChunkFromNew(chunk){
    this._newChunks.delete(chunk);
  },

  removeChunkFromUpdated(chunk){
    this._updatedChunks.delete(chunk);
  },

  dropChunkHash(object, frame, chunk) {
    switch (object.type) {
      case objectType.FUSION:
        object.getProperty("geometriesfile")["hash"] = "";
        break;
      case objectType.SCAN:
        object.getProperty("framesfile")["hash"] = "";
        break;
      case objectType.SURFELS:
        object.getProperty("surfelsfile")["hash"] = "";
        break;
    }
    const frameInfo = object.getProperty("frames").find(frameInfo => frameInfo.getValue("id") === frame.getID());
    const chunkInfo = frameInfo.getValue("geometries").find(chunkInfo => chunkInfo.getValue("id") === chunk.getID());

    for (const levelInfo of chunkInfo.getValue("levels")) {
      levelInfo.setValue("hash", "");
    }
  },

  async getObjectBuffersToSave(object) {
    if (objState.checkIsUpdating(object)) {
      await objState.getUpdatePromise(object);
    }

    const framesProperty = object.getProperty("frames");
    const promises = [];
    for (const frame of object.frames) {
      const frameProperty = framesProperty.find(property => property.getValue("id") === frame.getID());

      if (!frameProperty) {
        output.error(output.DEVELOPERS_ERROR, "Can't find frame " + frame.getID() + " in project sjson");
        continue;
      }

      const model = frame.model;
      const frameID = frame.getID();
      const chunks = model.chunks;

      for (const chunk of chunks) {
        const chunkID = chunk.getID();
        const geometryProperty = frameProperty.getValue("geometries").find(property => property.getValue("id") === chunkID);
        if (!geometryProperty) {
          output.error(output.DEVELOPERS_ERROR, "Can't find chunk " + chunkID + " in project sjson");
          continue;
        }

        let chunkFullID = frameID + "-" + chunkID;
        if (object.type === objectType.SCAN || object.type === objectType.CAD) {
          chunkFullID = object.getID() + "-" + chunkFullID;
        }

        const chunkPaths = [];
        const levelsProperty = geometryProperty.getValue("levels");
        if (_useChunkLevels && object.type !== objectType.FUSION) {
          const levelProperty = levelsProperty[object.geometryLevel];
          chunkPaths.push(levelProperty.getValue("file"));
        } else {
          for (const levelProperty of levelsProperty) {
            chunkPaths.push(levelProperty.getValue("file"));
          }
        }
        if (geometryProperty.getValue("attrs")) {
          chunkPaths.push(geometryProperty.getValue("attrs")["file"]);
        }

        if (_useChunkLevels) {
          const levelProperty = levelsProperty[object.geometryLevel];
          const levelID = levelProperty.getValue("id");
          chunkFullID = chunkFullID + "-" + levelID;
        }

        if (this.isNewChunk(chunk)) {
          this.getObjectFiles(object, _saveChunksState.projectFiles);

          promises.push(saveManager.getPackedBuffers(chunk, saveManager.ENCODER_ZSTD).then((buffer) => {
            const addPayload = {"chunkId": chunkFullID, "buffer": buffer, "paths": chunkPaths, "state": "new"};
            _saveChunksState.addPayloads.push(addPayload);
            this.removeChunkFromNew(chunk);
          }));
        }

        if (this.isUpdatedChunk(chunk)) {
          this.getObjectFiles(object, _saveChunksState.projectFiles);

          promises.push(saveManager.getPackedBuffers(chunk, saveManager.ENCODER_ZSTD).then((buffer) => {
            const updatePayload = {"chunkId": chunkFullID, "buffer": buffer, "paths": chunkPaths, "state": "updated"};
            _saveChunksState.updatePayloads.push(updatePayload);
            this.removeChunkFromUpdated(chunk);
            chunk.geometry.deleteLayoutItem("old_indices");
          }));
        }
      }
    }

    const updatePromise = Promise.all(promises).then(() => {
      this._removeUpdatePromise(object);
    });
    this._storeUpdatePromise(object, updatePromise);

    await updatePromise;
  },

  _calculateNeighbourChunks(chunks) {

    for (let j = 0; j < chunks.length; ++j) {

      const currChunk = chunks[j];
      const currBbox = currChunk.geometry.bbox;

      const childrenNeighbours = [];
      const parentNeighbours = [];

      this._chunksChildrenNeighbours.set(currChunk, childrenNeighbours);
      this._chunksParentNeighbours.set(currChunk, parentNeighbours);

      const currX = (currChunk.geometry.bbox[0] + currChunk.geometry.bbox[3]) / 2;
      const currY = (currChunk.geometry.bbox[1] + currChunk.geometry.bbox[4]) / 2;
      const currZ = (currChunk.geometry.bbox[2] + currChunk.geometry.bbox[5]) / 2;

      for (let i = 0; i < chunks.length; ++i) {

        const chunk = chunks[i];

        if (chunk == currChunk) {
          continue;
        }

        const bbox = chunk.geometry.bbox;

        const x = (chunk.geometry.bbox[0] + chunk.geometry.bbox[3]) / 2;
        const y = (chunk.geometry.bbox[1] + chunk.geometry.bbox[4]) / 2;
        const z = (chunk.geometry.bbox[2] + chunk.geometry.bbox[5]) / 2;

        if ( doBboxesIntersect(bbox, currBbox)) {

          const diffX = x - currX;
          const diffY = y - currY;
          const diffZ = z - currZ;

          const isParent = diffZ > 0 || diffZ==0 && diffY > 0 || diffZ == 0 && diffY == 0 && diffX > 0;

          if (isParent) {
            parentNeighbours.push(chunk);
          } else {
            childrenNeighbours.push(chunk);
          }

        }

      }

    }

  },

  getNeighbourChunks(chunk) {
    return this._chunksChildrenNeighbours.get(chunk);
  },

  setNeedsDupliVerticesUpdate(chunk, needs) {
    if (needs) {
      this._needDupliVerticesUpdate.add(chunk);
    } else {
      this._needDupliVerticesUpdate.delete(chunk);
    }
  },

  checkNeedsDupliVerticesUpdate(chunk) {
    return this._needDupliVerticesUpdate.has(chunk);
  },

  checkIsReady(object) {
    return object.loaded && !objState.checkIsUpdating(object);
  },

  setScene(scene) {
    this._scene = scene;
  },

  getScene() {
    return this._scene;
  },

  _getObjectSurfelsFile(object) {
    const surfelsDescription = object.getProperty("surfelsfile");
    return surfelsDescription ? surfelsDescription["jsonref"] : null;
  },

  _getObjectTrianglesFile(object) {
    if (object.type === objectType.FUSION) {
      return object.getProperty("geometriesfile")["jsonref"];
    } else {
      return object.getProperty("framesfile")["jsonref"];
    }
  },

  getObjectFiles(object, files) {
    const trianglesFile = this._getObjectTrianglesFile(object);
    if (trianglesFile) {
      files.push(trianglesFile);
    }
    const surfelsFile = this._getObjectSurfelsFile(object);
    if (surfelsFile) {
      files.push(surfelsFile);
    }
  },

  getPhotoGroupFiles(photoGroup, files) {
    files.push(photoGroup.getProperty("photosSource")["jsonref"]);
  },

  getCadSlice(object) {
    const slice = object.getProperty("cadModel")["file"];
    return slice;
  },

  //Note: this method is only needed for server-side manualalign
  async getSurfelsObjectFrameGeometrySlices(object) {
    const tmpSurfelsFile = object.getProperty("surfelsfile");
    const tmpFrames = object.getProperty("frames");

    //_selfPropertySetter usage is a hack
    object._selfPropertySetter("surfelsfile", null);
    object._selfPropertySetter("frames", []);
    object._selfPropertySetter("initStatus", propertyInitStatus.INCOMPLETE);

    await projectTree.combineObjectStructure(object.getID());
    const surfelsObjectFrameGeometrySlices = object.getProperty("frames");
    object._selfPropertySetter("surfelsfile", tmpSurfelsFile);
    object._selfPropertySetter("frames", tmpFrames);

    return surfelsObjectFrameGeometrySlices;
  },

  getObjectFramesProperty(object) {
    return object.getProperty("frames");
  },

  async getFrameGeometrySlices(frameProperty) {
    const slices = new Set();
    for (const geometry of frameProperty.getValue("geometries")) {
      const levels = geometry.getValue("levels");
      slices.add(levels[0].getValue("file"));
    }
    return Array.from(slices);
  },

  getFrameTextureSlices(object, frame=null) {
    let frameProperty;
    if (object.type === objectType.FUSION) {
      frameProperty = object.getProperty("frames")[0];
    } else {
      frameProperty = object.getProperty("frames").find(property => property.getValue("id") === frame.getID());
    }

    const slices = new Set();
    for (const texture of frameProperty.getValue("textures")) {
      const levels = texture.getValue("levels");
      slices.add(levels[0].getValue("file"));
    }
    return Array.from(slices);
  },

  _initDecimationMap() {
    this._sliceToFrames.clear();

    for (const object of this._objects) {
      if (!this._objectInitialized.has(object)) {
        continue;
      }

      const noDecimationFrame = object.hasTextures() ? object.frames.find(frame => frame.hasTextures()) : object.frames[0];
      for (const frameProperty of object.getProperty("frames")) {
        const frame = object.frames.find(frame => frame.getID() === frameProperty.getValue("id"));

        for (const geometryPropery of frameProperty.getValue("geometries")) {
          const levels = geometryPropery.getValue("levels");
          const filepath = levels[0].getValue("file");
          let framesSet = this._sliceToFrames.get(filepath);
          if (!framesSet) {
            framesSet = new Set();
            this._sliceToFrames.set(filepath, framesSet);
            if (object.type === objectType.SCAN) {
              this._decimatedFramesSets.add(framesSet);
            }
          }

          if (frame.getID() === noDecimationFrame.getID()) {
            this._decimatedFramesSets.delete(framesSet);
          }

          framesSet.add(frame);

        }
      }
    }
  },

  _updateDecimationMap() {

    let changed = false;

    const slicesToLoad = this._scansDecimationRatio * this._sliceToFrames.size;
    let slicesLoaded = 0;

    const allFramesSets = Array.from(this._sliceToFrames.values());
    const texturedFramesSet = allFramesSets.filter(framesSet => {
      const firstFrame = framesSet.values().next().value;
      return firstFrame.textureList.length > 0;
    });
    const nonTexturedFramesSet = allFramesSets.filter(framesSet => {
      const firstFrame = framesSet.values().next().value;

      return firstFrame.textureList.length === 0;
    });

    for (const framesSet of allFramesSets) {
      const firstFrame = framesSet.values().next().value;
      const isTexturedFramesSet = firstFrame.textureList.length > 0;
      for (const frame of framesSet) {
        const isTexturedFrame = frame.textureList.length > 0;
        if (isTexturedFramesSet != isTexturedFrame) {
          output.warn("There are textured and untextured frames in the same slice.");
          break;
        }
      }
    }

    const processedSets = new Set();
    let priority = 1;
    const decimateFramesSet = framesSets => {
      let step = framesSets.length - 1 || 1;

      while (step > 0) {
        for (let i = 0; i < framesSets.length; i += step) {
          const framesSet = framesSets[i];
          if (processedSets.has(framesSet)) {
            continue;
          }

          const decimationAllowed = this._decimatedFramesSets.has(framesSet);
          const firstFrame = framesSet.values().next().value;

          const sliceAlreadyLoaded = firstFrame.shouldBeLoaded;
          const sliceShown = firstFrame ? firstFrame.getVisibility() : false;
          const sliceShouldBeLoaded = (sliceAlreadyLoaded || sliceShown) && (!decimationAllowed || slicesLoaded < slicesToLoad);

          for (const frame of framesSet) {
            if (frame.shouldBeLoaded !== sliceShouldBeLoaded) {
              changed = true;
            }

            frame.shouldBeLoaded = sliceShouldBeLoaded;
            this._frameToPriority.set(frame, decimationAllowed ? priority : 1);
          }
          priority++;

          if (sliceShouldBeLoaded) {
            slicesLoaded++;
          }

          processedSets.add(framesSet);
        }

        step = Math.floor(step / 2);
      }
    }

    decimateFramesSet(texturedFramesSet);
    decimateFramesSet(nonTexturedFramesSet);
    for (const object of this.getPhotoGroups().concat(this.getMeasures())) {
      if (object.isLoadingNeeded()) changed = true;
    }

    return changed;

  },

  startProjectLoading() {
    return this._updateAllNonTexturedFrames();
  },

  _updateAllNonTexturedFrames() {
    for (const object of this._objects) {
      this.updateFramesVisibility(object);
    }
    return this.updateDecimation();
  },

  updateDecimation() {
    const changed = this._updateDecimationMap();
    if (!changed) {
      return Promise.resolve();
    }

    loadingProgress.init();
    return this.reloadObjects();
  },

  shiftLoadingQueue(queue) {
    //Note: whole asset group has same priority

    for (const assetGroup of queue) {
      if (assetGroup.type !== loader.GEOMETRY || !assetGroup.assets.length) {
        continue;
      }

      const asset = assetGroup.assets[0];
      const objectID = asset.payload.objectID;
      const frameID = asset.payload.frameID;

      const object = this.getObjectByID(objectID);
      if (!object) {
        continue;
      }

      const frame = object.frames.find(frame => frame.getID() === frameID);
      if (!frame) {
        continue;
      }

      assetGroup.priority = this._frameToPriority.get(frame);
    }
  },

  async reloadObjects() {
    appState.setStateValue("isLoading", true);
    const promises = [];

    for (const object of this.getObjects()) {

      object.unloadMarkedFrames();

      if (objState.checkIsUpdating(object)) {
        continue;
      }

      if (object.checkNeedsLoadFrames()) {
        const fusionHasLods = object.type === objectType.FUSION && this.hasObjectLods(object);
        if (object.type === objectType.SCAN || object.type === objectType.SURFELS) monitoring.registerLoadingObject();
        let promise;
        if (config.lodPreload && fusionHasLods) {
          const fastLevel = 1;
          const defaultLevel = 0;
          promise = this.loadObjectData(object, fastLevel).then(() => {
            const frame = object.frames[0];
            //NOTE: we have to release the flags to reload object geometry
            frame.loaded = false;
            for (const chunk of frame.model.chunks) {
              chunk.needsReload = true;
            }
            return this.loadObjectData(object, defaultLevel);
          });
        } else {
          const level = object.type === objectType.SURFELS && appState.getStateValue("scanMode") === scanMode.SURFELS_MID && this.hasObjectLods(object) ? 1 : 0;
          promise = this.loadObjectData(object, level);
        }
        promise = promise.finally(() => {
          if (object.type === objectType.SCAN || object.type === objectType.SURFELS) monitoring.unregisterLoadingObject();
        });

        promises.push(promise);
      }
    }

    for (const object of this.getPhotoGroups().concat(this.getMeasures())) {
      if (!object.isLoadingNeeded()) continue;

      promises.push(object.loadData());
    }

    await Promise.all(promises);

    appState.setStateValue("isLoading", false);
  },

  setScansDecimationRatio(rate) {

    this._scansDecimationRatio = rate;

    this.updateDecimation();
  },

  getScansDecimationRatio() {
    return this._scansDecimationRatio;
  },

  /*
  createGroup(name, id, groupID) {
    const property = reformer.createGroupProperty();

    property.insertValue("name", name);
    property.insertValue("groupID", groupID);
    property.insertValue("id", id);

    const group = this._createGroupWrapper(property);
    return group;
  },
   */

  _createGroupWrapper(property) {
    const group = new Group({
      id : property.getValue("id"),
    });

    this._groups.push(group);
    return group;
  },

  removeGroup(groupID) {
    const group = this.getGroupByID(groupID);
    if (!group) {
      output.error(output.API_ERROR, "Can't remove group. Wrong group id: \"" + groupID + "\"");
      return;
    }
    const payload = {
      id: group.getID(),
      uuid: group.getUUID(),
      name: group.getName(),
      project_files: [],
    }

    this._removeGroup(group);
    return payload;
  },

  _removeGroup(group) {
    const groups = this._groups;
    for (const object of this.getObjects().concat(groups).concat(this.getPhotoGroups())) {
      if (object.getGroup() === group.getID()) {
        object.setGroup(group.getGroup());
      }
    }

    groups.splice(groups.indexOf(group), 1);
    projectTree.removeObject(group.getID());
  },

  _removePhotoGroup(photoGroup) {
    this._photoGroups.splice(this._photoGroups.indexOf(photoGroup), 1);
    projectTree.removeObject(photoGroup.getID());
  },

  _removeMeasure(measure) {
    this._measures.splice(this._measures.indexOf(measure), 1);
    projectTree.removeObject(measure.getID());
  },

  hasObjectLods(object) {
    const geometriesInfo = object.getProperty("frames")[0].getValue("geometries");
    for (const geometryInfo of geometriesInfo) {
      if (geometryInfo.getValue("levels").length > 1) return true;
    }

    return false;
  },

  hasScanSurfels(scan) {
    return scan.getProperty("surfelsfile") !== null;
  },

  async setObjectVisibility(object, visible) {
    object.setVisibility(visible);
    if (object instanceof DetachedWrapper) return;

    if (visible) await this.initObjects();

    if (object instanceof ObjectWrapper) {
      if (visible) {
        await this.updateNonTexturedFrames(object);
      } else {
        releaseSlicedCache(object);
      }

      reportGeometrySelection(object);

      this.applyState(object);

      events.emit("object_visibility_change");
    } else {
      await this.updateDecimation();
    }

  },

  duplicateObjects(objectIDs) {

    const payload = {
      "operations" : [],
      "projectFiles" : []
    };
    const operations = payload["operations"];
    const projectFiles = [];
    const groupIdMap = {};

    for (const id of objectIDs) {
      const object = this.getObjectByID(id) || this.getGroupByID(id) || this.getPhotoGroupByID(id);
      if (!object) {
        output.warn(`Couldn't copy object. Unknown id: ${id}`);
        continue;
      }

      if (!(object.getGroup() in groupIdMap)) {
        groupIdMap[object.getGroup()] = [];
      }

      groupIdMap[object.getGroup()].push(object);
    }

    for (const groupID in groupIdMap) {
      const entry = {"op": "copyEntry",
                     "objects": []};

      if (groupID) {
        entry["groupID"] = groupID;
      }
      for (const object of groupIdMap[groupID]) {
        entry["objects"].push(this._generateObjectCopyEntry(object, projectFiles));
      }

      operations.push(entry);
    }

    payload["projectFiles"] = [... new Set(projectFiles)];

    return payload;

    // NOTE: This might come in handy when objects duplication is done in RAM
    /*
    const duplicatedData = duplicateObjectData(data);

    let newObject;
    switch (object.type) {
      case objectType.SCAN: {
        newObject = this._createScanWrapper(duplicatedData);
        for (let i = 0; i < object.frames.length; ++i) {
          if (!object.frames[i].visible) {
            continue;
          }
          newObject.frames[i].initChunks(duplicatedData["frames"][i]["geometries"]);
        }

        break;
      }

      case objectType.FUSION: {
        newObject = this._createFusionWrapper(duplicatedData);
        newObject.frames[0].initChunks(duplicatedData["geometries"]);

        break;
      }

      case objectType.SURFELS: {
        newObject = this._createSurfelsWrapper(duplicatedData);
        newObject.frames[0].initChunks(duplicatedData["geometries"]);

        break;
      }
    }

    this._cloneObjectGeometry(newObject, object);

    newObject.setVisibility(object.visible);
    newObject.locked = object.locked;
    newObject.model.setMatrix(object.model.modelMatrix);

    const objInfo = {
      "copyOf" : object.getID()
    };
    this._updateObjectInfo(newObject, objInfo);
    this._projectContent.objects.push(objInfo);

    this.onObjectLoaded(newObject);

    return object;
    */

  },

  generateUniqueGroupName() {

    const groupsNames = new Set();

    for (let i = 0; i < this._groups.length; ++i) {
      groupsNames.add(this._groups[i].getName());
    }

    let n = 0;
    let name = "Group_" + n;

    while (groupsNames.has(name)) {
      ++n;
      name = "Group_" + n;
    }

    return name;

  },

  generateUniqueGroupID() {

    const groupIDs = new Set();

    for (const group of this.getGroups()) {
      groupIDs.add(group.getID());
    }

    for (const object of this.getObjects().concat(this.getPhotoGroups()).concat(this.getMeasures())) {
      groupIDs.add(object.getID());
    }

    let n = 0;
    let id = "n" + n.toString().padStart(2, "0");

    while (groupIDs.has(name)) {
      ++n;
      id = "n" + n.toString().padStart(2, "0");
    }

    return id;

  },

  _resetChunkDupliVertices(chunk) {

    chunk.resetDupliVertices();
    chunk.resetParentVerticesMask();

    const parents = this._chunksParentNeighbours.get(chunk);
    for (let i = 0; i < parents.length; ++i) {
      parents[i].resetDupliVertices();
      this.setNeedsDupliVerticesUpdate(parents[i], true)
    }

    const children = this._chunksChildrenNeighbours.get(chunk);

    for (let i = 0; i < children.length; ++i) {

      const childChunk = children[i];
      childChunk.resetParentVerticesMask();
      this.setNeedsDupliVerticesUpdate(childChunk, true)

      const childParents = this._chunksParentNeighbours.get(childChunk);
      for (let j = 0; j < childParents.length; ++j) {
        childParents[j].resetDupliVertices();
        this.setNeedsDupliVerticesUpdate(childParents[j], true)
      }

    }

  },

  _cloneObjectGeometry(dstObject, srcObject) {

    const srcFrames = srcObject.frames;
    const dstFrames = dstObject.frames;

    for (let i = 0; i < dstFrames.length; ++i) {

      const srcFrame = srcFrames[i];
      const dstFrame = dstFrames[i];

      const srcFrameModel = srcFrames[i].model;
      const dstFrameModel = dstFrames[i].model;

      const srcChunks = srcFrameModel.chunks;
      const dstChunks = dstFrameModel.chunks;

      for (let j = 0; j < dstChunks.length; ++j) {

        const srcChunk = srcChunks[j];
        const dstChunk = dstChunks[j];

        const srcGeometry = srcChunk.geometry;
        const dstGeometry = cloneGeometry(srcGeometry);

        dstFrameModel.setChunkGeometry(dstChunk, dstGeometry);
        dstFrameModel.updateChunkBBox(dstChunk);

        this.markChunkAsNew(dstChunk);

      }

      dstFrame.numPolygons = srcFrame.numPolygons;
      dstFrame.numVertices = srcFrame.numVertices;
      dstFrame.loaded = true;

    }

  },

  prepareWireframeGeometry(object, enabled) {

    if (!object.loaded) {
      return;
    }

    for (const frame of object.frames) {
      if (frame.loaded) {
        for (const chunk of frame.model.chunks) {
          chunk.updateVertexID(enabled);
        }
      }
    }

  },

  getVerbosityInfo() {
    let info = "";

    for (const object of this.getObjects()) {
      info += this.getVerbosityAboutObject(object);
    }

    return info;
  },

  getVerbosityAboutObject(object) {
    let chunkOctreeUpdated = false;
    // NOTE: come up about this info
    // if (this.checkIsReady(object)) {
    //   chunkOctreeUpdated = true;
    //   for (const frame of object.frames) {
    //     for (const chunk of frame.model.chunks) {
    //       if (this.checkNeedsOctreeUpdate(chunk)) {
    //         chunkOctreeUpdated = false;
    //       }
    //     }
    //   }
    // }

    return `object: ${object.id}, type: ${object.type}, visibility: ${object.getVisibility()}, isLocked: ${object.getLockState()}, isLoaded: ${object.loaded}, isUpdating: ${objState.checkIsUpdating(object)}, octreeBuilt: ${object.getState(appState.GS_OCTREE)}, chunksOctreeUpdated: ${chunkOctreeUpdated}\n`;
  },


  updateShadesForGroupObjects(group, enabled) {
    const objectsInGroup = this.getObjects().concat(this.getPhotoGroups()).filter(object => object.getGroup() === group.getID());
    const groupColor = group.getColor();

    const {h, s, l} = colorUtils.rgbToHsl({r: groupColor[0], g: groupColor[1], b: groupColor[2]});
    const shadesCount = objectsInGroup.length + 1;
    const step = (1 - l) / shadesCount;

    const frameColorEnabled = appState.getStateValue("colorMode") === appState.CM_FRAME_COLOR;

    for (let i = 0; i < objectsInGroup.length; i++) {
      const object = objectsInGroup[i];
      const newObjectColor = colorUtils.hslToRgb({h, s, l: l + step * (i+1)});
      if (enabled) {
        object.applyTemporaryColorToFrames(newObjectColor);
      } else if (frameColorEnabled) {
        object.applyUniqueColorToFrames();
      } else {
        object.applyObjectColorToFrames();
      }
    }
  },

  updateColorsForGroupObjects(group, enabled) {
    const objectsInGroup = this._objects.filter(object => object.getGroup() === group.getID());
    const groupColor = group.getColor();
    const frameColorEnabled = appState.getStateValue("colorMode") === appState.CM_FRAME_COLOR;

    for (const object of objectsInGroup) {
      if (enabled) {
        object.applyTemporaryColorToFrames(groupColor);
      } else if (frameColorEnabled) {
        object.applyUniqueColorToFrames();
      } else {
        object.applyObjectColorToFrames();
      }
    }
  },

  async removeObjects(descriptors) {
    const payload = {
      "projectFiles": [],
    }
    const projectFiles = payload["projectFiles"];
    const dataFiles = [];

    for (const desc of descriptors) {
      const objectID = desc.objectID;
      const object = this.getObjectByID(objectID) || this.getGroupByID(objectID) || this.getPhotoGroupByID(objectID);
      if (!object) {
        output.error(output.API_ERROR, "Can't remove object " + objectID + ". Not found");
        continue;
      }

      await this._recursiveRemoveObject(object, desc.l1ID, projectFiles, dataFiles);
    }

    payload["projectFiles"] = [...new Set(projectFiles)];
    if (dataFiles.length) payload["dataFiles"] = [...new Set(dataFiles)];
    return payload;
  },

  async _recursiveRemoveObject(object, l1ID, projectFiles, dataFiles) {
    if (object.type === objectType.PHOTO_GROUP) {
      this.getPhotoGroupFiles(object, projectFiles);
      if (l1ID !== undefined) {
        object.removePhoto(l1ID, dataFiles);
      } else {
        this._removePhotoGroup(object);
      }
    } else if (object.type === objectType.GROUP) {
      const children = this._getGroupChildren(object);
      for (const child of children) {
        await this._recursiveRemoveObject(child, l1ID, projectFiles, dataFiles);
      }
      this._removeGroup(object);
    } else {
      this.getObjectFiles(object, projectFiles);
      await this.removeObject(object);
    }
  },

  getChunkPath(object, frameID, chunkID) {
    const frame = object.frames.find(frame => frame.getID() === frameID);
    if (!frame) {
      output.error(output.DEVELOPERS_ERROR, "Can't find frame " + frameID + " in workspace");
      return;
    }

    const frameProperties = object.getProperty("frames");
    if (!frameProperties) return;

    const frameProperty = frameProperties.find(property => property.getValue("id") === frameID);
    if (!frameProperty) return;

    const geometryProperty = frameProperty.getValue("geometries").find(property => property.getValue("id") === chunkID);
    if (!geometryProperty) {
      output.error(output.DEVELOPERS_ERROR, "Can't find chunk " + chunkID + " in project sjson");
      return;
    }

    const levels = geometryProperty.getValue("levels");

    return levels[0].getValue("file");
  },

  _generateObjectCopyEntry(object, projectFiles) {
    const entry = {
                    "visibility": object.getVisibility(),
                    "color":      reformer.generateHexColorRandom()
                  };
    const newName = "Copy of " + object.getName();

    entry["id"] = object.getID();
    if (object.type === objectType.GROUP) {
      const children = this._getGroupChildren(object);
      entry["objects"] = children.map(child => this._generateObjectCopyEntry(child, projectFiles));
      entry["groupName"] = newName;
    } else if (object.type === objectType.PHOTO_GROUP) {
      this.getPhotoGroupFiles(object, projectFiles);
      entry["name"] = newName;
    } else {
      this.getObjectFiles(object, projectFiles);
      entry["name"] = newName;
    }

    return entry;
  },

  _getGroupChildren(group) {
    return this.getObjects().concat(this._groups).concat(this._photoGroups).filter(obj => obj.getGroup() === group.id);
  },

  async updateSaveChunksState() {
    const promises = [];
    for (const object of this.getObjects()) {
      promises.push(this.getObjectBuffersToSave(object));

      const frames = object.getProperty("frames");
      for (const frame of object.frames) {
        const frameProperty = frames.find(property => property.getValue("id") === frame.getID());

        if (!frameProperty) {
          output.error(output.DEVELOPERS_ERROR, "Can't find frame " + frame.getID() + " in project sjson");
          continue;
        }

        for (let deletedChunkID of frame.chunksToBeRemoved) {
          const geometryProperty = frameProperty.getValue("geometries").find(property => property.getValue("id") === deletedChunkID);
          if (!geometryProperty) {
            output.error(output.DEVELOPERS_ERROR, "Can't find chunk " + deletedChunkID + " in project sjson");
            continue;
          }

          let chunkFullID = frame.getID() + "-" + deletedChunkID;
          if (object.type === objectType.SCAN || object.type === objectType.CAD) {
            chunkFullID = object.getID() + "-" + chunkFullID;
          }

          const chunkPaths = [];
          const levelsProperty = geometryProperty.getValue("levels");
          if (_useChunkLevels && object.type !== objectType.FUSION) {
            const levelProperty = levelsProperty[object.geometryLevel];
            chunkPaths.push(levelProperty.getValue("file"));
          } else {
            for (const levelProperty of levelsProperty) {
              chunkPaths.push(levelProperty.getValue("file"));
            }
          }
          if (geometryProperty.getValue("attrs")) {
            chunkPaths.push(geometryProperty.getValue("attrs")["file"]);
          }

          if (_useChunkLevels) {
            const levelProperty = levelsProperty[object.geometryLevel];
            const levelID = levelProperty.getValue("id");
            chunkFullID = chunkFullID + "-" + levelID;
          }

          this.getObjectFiles(object, _saveChunksState.projectFiles);

          const deletePayload = {"chunkId": chunkFullID, "paths": chunkPaths, "state": "deleted"};
          _saveChunksState.deletePayloads.push(deletePayload);
        }

        if (frame.model.chunks.length === 0 && frame.chunksToBeRemoved.length > 0) {
          const frameFullID = object.type === objectType.SCAN || object.type === objectType.CAD ? object.getID() + "-" + frame.getID() : frame.getID();
          processFrameDeletion(frameProperty, frameFullID);
        }
        frame.chunksToBeRemoved.length = 0;
      }
    }

    try {
      await Promise.all(promises);
    } catch (error) {
      output.error(output.DEVELOPERS_ERROR, "Can't pack chunks. Reason: " + error);
    }
  },

  saveChunks() {
    const saveId = this._saveID++;

    const slicedMode = projectTree.version >= 4;
    events.emit("save", {"start": true, "saveId": saveId});
    appState.setStateValue("isSaving", true);

    generateSaveEventsForChunks(slicedMode, saveId);

    appState.setStateValue("isSaving", false);
    events.emit("save", {"end": true, saveId});
  },

  saveJSON() {
    const saveId = this._saveID++;

    events.emit("save", {"start": true, "saveId": saveId});
    appState.setStateValue("isSaving", true);

    //TODO: WS-337
    const serializedJson = reformer.getSerializedProject();
    events.emit("save", {"sjson": serializedJson, "saveId": saveId});

    appState.setStateValue("isSaving", false);
    events.emit("save", {"end": true, saveId});
  },

  getObjectMaxError(objectID) {
    const object = this.getObjectByID(objectID);
    if (!object) {
      output.error(output.API_ERROR, `Can't get object's max error. Object with id "${objectID}" is not found`);
      return null;
    }

    if (object.type !== objectType.SCAN && object.type !== objectType.SURFELS) {
      return null;
    }

    const maxError = object.getProperty("maxError");
    const scannerType = object.getScannerType();
    const maxErrorColor = colorUtils.getErrorColor(maxError, scannerType, []);
    return {
      color: maxErrorColor,
      maxError: maxError,
    };
  },

  activateObjectOutline(object, outlinePass, highlightPreset) {
    const shader = object.model.shader;
    outlinePass.setHighlightSettings(highlightPreset);

    this.scheduleObjectDrawQueries(object, false);

    shader.enableStencilTest();
    shader.setStencilFrontZPassAction(stencilAction.REPLACE);
    shader.setStencilBackZPassAction(stencilAction.REPLACE);
    shader.setStencilFrontZFailAction(stencilAction.REPLACE);
    shader.setStencilBackZFailAction(stencilAction.REPLACE);
    shader.setStencilReferenceValue(stencilValue.HIGHLIGHTED_OBJECT);
    shader.setStencilFunction(stencilFunc.ALWAYS);
  },

  deactivateObjectOutline(object) {
    object.model.shader.disableStencilTest();
    this.scheduleObjectDrawQueries(object, true);
  },
}

function doBboxesIntersect(a, b) {
  return (a[0] <= b[3] && a[3] >= b[0]) &&
         (a[1] <= b[4] && a[4] >= b[1]) &&
         (a[2] <= b[5] && a[5] >= b[2]);
}

// NOTE: This might come in handy when objects duplication is done in RAM
/*
function duplicateObjectData(data) {

  const JSONData = JSON.stringify(data);
  const duplicatedData = JSON.parse(JSONData);

  duplicatedData["id"] = idGenerator.getNext();

  const frames = duplicatedData["frames"];

  if (frames) {
    for (let i = 0; i < frames.length; ++i) {
      dropGeometryDataLevels(frames[i]);
    }
  } else {
    dropGeometryDataLevels(duplicatedData);
  }

  return duplicatedData;

}

function dropGeometryDataLevels(data) {

  const geometriesData = data["geometries"];

  for (let i = 0; i < geometriesData.length; ++i) {
    const geometryData = geometriesData[i];
    geometryData["levels"] = [];
  }

}
*/

function cloneGeometry(srcGeometry) {

  const srcLayout = srcGeometry.getLayout();

  const layout = {};
  const bbox = srcGeometry.bbox.slice();

  for (let bufferName in srcLayout) {

    const srcItem = srcLayout[bufferName];

    const dstItem = new GeometryBuffer({
      buffer:     srcItem.buffer.slice(),
      type:       srcItem.type,
      byteOffset: srcItem.getByteOffset(),
      stride:     srcItem.getStride(),
      divisor:    srcItem.getDivisor()
    });

    layout[bufferName] = dstItem;

  }

  return new Geometry({layout, bbox});

}

function releaseSlicedCache(object) {
  const textureLevel = object.type === objectType.FUSION ? 0 : config.scansTextureLevel;

  for (const frameProperty of object.getProperty("frames")) {
    for (const geometry of frameProperty.getValue("geometries")) {
      const levels = geometry.getValue("levels");
      if (object.geometryLevel === null || levels.length <= object.geometryLevel) continue;

      const filename = levels[object.geometryLevel].getValue("file");
      containersCache.releaseContainer(reformer.getProjectDirectory() + filename);
    }

    for (const texture of frameProperty.getValue("textures")) {
      const levels = texture.getValue("levels");
      const filename = levels[textureLevel].getValue("file");
      containersCache.releaseContainer(reformer.getProjectDirectory() + filename);
    }
  }
}

function updateModelTransform(model, data) {
  if (config.viewOnlyMode) return false;

  const eps = 0.000001;
  let upToDate = true;

  for (let i=0; i<16; i++) {
    if (Math.abs(model.localMatrix[i] - data.getValue("transform")[i]) > eps) {
      upToDate = false;
      break;
    }
  }

  if (!upToDate) {
    model.setMatrix(data.getValue("transform"));
    model.updateMatrix();
  }

  return !upToDate;
}

async function _updatePhotoGroup(photoGroup, oldProperty, property) {
  if (oldProperty.getValue("initStatus") !== propertyInitStatus.COMPLETE) return false;

  const oldGeometryHash = oldProperty.getValue("photosSource")["hash"];
  const newGeometryHash = property.getValue("photosSource")["hash"];
  const geometryUpToDate = oldGeometryHash === newGeometryHash;

  if (geometryUpToDate) {
    property.setValue("photos", oldProperty.getValue("photos"));
    property.setValue("initStatus", propertyInitStatus.COMPLETE);
  } else {
    photoGroup.unloadData();
    await projectTree.combineObjectStructure(photoGroup.getID());
  }

  return !geometryUpToDate;
}

async function _updateMeasure(measure, oldProperty, newProperty) {
  if (oldProperty.getValue("initStatus") !== propertyInitStatus.COMPLETE) return false;

  newProperty.setValue("selected", oldProperty.getValue("selected"));
  if (oldProperty.getValue("geometriesSource")["hash"] === newProperty.getValue("geometriesSource")["hash"]) {
    newProperty.setValue("geometries", oldProperty.getValue("geometries"));
    newProperty.setValue("properties", oldProperty.getValue("properties"));

    if (newProperty.getValue("type") === objectType.SECTION) {
      for (let i=0; i<oldProperty.getValue("geometries").length; i++) {
        const oldSection = oldProperty.getValue("geometries")[i];
        const newSection = newProperty.getValue("geometries")[i];

        for (let j=0; j<oldSection["contours"].length; j++) {
          const oldContour = oldSection["contours"][j];
          const newContour = newSection["contours"][j];

          newContour["selected"] = oldContour["selected"];
        }
        newSection["selected"] = oldSection["selected"];
      }
    }
  } else {
    measure.unloadData();
    await projectTree.combineObjectStructure(measure.getID());
  }
}

function reportGeometrySelection(object) {
  for (const frame of object.frames) {
    for (const chunk of frame.model.chunks) {
      if (!chunk.geometry) {
        continue;
      }

      const selectionOverride = object.getVisibility() ? chunk.geometry.checkHasSelectedVertices() : false;
      events.emit("geometry_selection_changed", {geometry: chunk.geometry, isSelected: selectionOverride});
    }
  }
}

function processFrameDeletion(property, fullID) {

  const textures = property.getValue("textures");
  const trackings = property.getValue("trackings");

  const frameFiles = [];

  for (const tracking of trackings) {
    for (const trackingLevel of tracking["levels"]) {
      frameFiles.push(trackingLevel["file"]);
    }
  }

  for (const texture of textures) {
    for (const level of texture.getValue("levels")) {
      frameFiles.push(level.getValue("file"));
    }
  }

  if (frameFiles.length > 0) {
    _saveChunksState.deletedFrames.push({"id": fullID, "paths": frameFiles});
  }
}

function generateSaveEventsForChunks(slicedMode, saveId) {
  const savePayload = {
    "saveId": saveId,
    "sliceDiffs": [],
    "projectFiles": [... new Set(_saveChunksState.projectFiles)],
  };

  if (slicedMode) {
    const sliceDiffsMap = new Map();
    for (const payloadGroup of [_saveChunksState.addPayloads, _saveChunksState.updatePayloads, _saveChunksState.deletePayloads]) {
      for (const payload of payloadGroup) {
        const chunkBuffer = payload["buffer"];
        const slicePaths = payload["paths"];
        let currentSliceDiff = sliceDiffsMap.get(slicePaths[0]);
        if (!currentSliceDiff) {
          currentSliceDiff = {
            "buffers": [],
            "chunks": [],
            "paths": new Set(),
          };
          sliceDiffsMap.set(slicePaths[0], currentSliceDiff);
        }

        if (chunkBuffer) {
          currentSliceDiff["buffers"].push(chunkBuffer);
        }

        const chunkMeta = {
          "chunkId": payload["chunkId"],
          "state": payload["state"],
        };
        currentSliceDiff["chunks"].push(chunkMeta);
        slicePaths.forEach(path => currentSliceDiff["paths"].add(path))
      }
    }

    const sliceDiffs = Array.from(sliceDiffsMap.values());
    for (const sliceDiff of sliceDiffs) {
      sliceDiff["paths"] = Array.from(sliceDiff["paths"]);
      sliceDiff["buffer"] = containersCache.packBuffers(sliceDiff["buffers"]);
      delete sliceDiff["buffers"];
    }

    savePayload["sliceDiffs"] = sliceDiffs;
    if (_saveChunksState.deletedFrames.length > 0) {
      savePayload["deletedFrames"] = _saveChunksState.deletedFrames;
    }

  } else {
    for (const payloadGroup of [_saveChunksState.addPayloads, _saveChunksState.updatePayloads, _saveChunksState.deletePayloads]) {
      for (const payload of payloadGroup) {
        const sliceDiff = {
          "chunks": []
        };

        const chunkInfo = {
          "chunkId": payload["chunkId"],
          "state": payload["state"],
        };

        if (payload["buffer"]) {
          sliceDiff["buffer"] = payload["buffer"];
        }

        sliceDiff["chunks"].push(chunkInfo);
        savePayload["sliceDiffs"].push(sliceDiff);
      }
    }
  }

  events.emit("save", savePayload);

  _saveChunksState.addPayloads.length = 0;
  _saveChunksState.updatePayloads.length = 0;
  _saveChunksState.deletePayloads.length = 0;
  _saveChunksState.deletedFrames.length = 0;
  _saveChunksState.projectFiles.length = 0;
}
